import React, { forwardRef, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { AlertCircle, HelpCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

// Base Field Wrapper
interface FieldWrapperProps {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  tooltip?: string;
  className?: string;
  children: React.ReactNode;
  htmlFor?: string;
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label,
  required,
  error,
  hint,
  tooltip,
  className,
  children,
  htmlFor,
}) => {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <div className="flex items-center justify-between">
          <label
            htmlFor={htmlFor}
            className="block text-sm font-medium text-slate-300"
          >
            {label}
            {required && (
              <span className="text-red-400 ml-0.5" aria-hidden="true">*</span>
            )}
          </label>
          {tooltip && (
            <button
              type="button"
              className="text-slate-500 hover:text-slate-300 transition-colors"
              title={tooltip}
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {children}
      {error && (
        <div className="flex items-center gap-1.5 text-red-400" role="alert">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-xs">{error}</span>
        </div>
      )}
      {hint && !error && (
        <p className="text-xs text-slate-500">{hint}</p>
      )}
    </div>
  );
};

// Input Field
interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  tooltip?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  wrapperClassName?: string;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      required,
      error,
      hint,
      tooltip,
      leftIcon,
      rightIcon,
      size = 'md',
      className,
      wrapperClassName,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;

    const sizeClasses = {
      sm: 'h-8 text-xs px-3',
      md: 'h-10 text-sm px-4',
      lg: 'h-12 text-base px-5',
    };

    const iconPadding = {
      sm: leftIcon ? 'pl-8' : '',
      md: leftIcon ? 'pl-10' : '',
      lg: leftIcon ? 'pl-12' : '',
    };

    return (
      <FieldWrapper
        label={label}
        required={required}
        error={error}
        hint={hint}
        tooltip={tooltip}
        className={wrapperClassName}
        htmlFor={inputId}
      >
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            className={cn(
              'w-full rounded-xl bg-slate-800/50 border text-white placeholder:text-slate-500',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error
                ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500'
                : 'border-white/10 hover:border-white/20',
              sizeClasses[size],
              iconPadding[size],
              rightIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
              {rightIcon}
            </div>
          )}
        </div>
      </FieldWrapper>
    );
  }
);

InputField.displayName = 'InputField';

// Select Field
interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  tooltip?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  wrapperClassName?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      required,
      error,
      hint,
      tooltip,
      options,
      placeholder,
      size = 'md',
      className,
      wrapperClassName,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;

    const sizeClasses = {
      sm: 'h-8 text-xs px-3',
      md: 'h-10 text-sm px-4',
      lg: 'h-12 text-base px-5',
    };

    return (
      <FieldWrapper
        label={label}
        required={required}
        error={error}
        hint={hint}
        tooltip={tooltip}
        className={wrapperClassName}
        htmlFor={selectId}
      >
        <select
          ref={ref}
          id={selectId}
          aria-required={required}
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-xl bg-slate-800/50 border text-white',
            'transition-all duration-200 appearance-none cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error
              ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500'
              : 'border-white/10 hover:border-white/20',
            sizeClasses[size],
            'pr-10',
            'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")] bg-[length:1.25rem] bg-[right_0.75rem_center] bg-no-repeat',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
      </FieldWrapper>
    );
  }
);

SelectField.displayName = 'SelectField';

// Textarea Field
interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  tooltip?: string;
  wrapperClassName?: string;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  (
    {
      label,
      required,
      error,
      hint,
      tooltip,
      className,
      wrapperClassName,
      id,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`;

    return (
      <FieldWrapper
        label={label}
        required={required}
        error={error}
        hint={hint}
        tooltip={tooltip}
        className={wrapperClassName}
        htmlFor={textareaId}
      >
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          aria-required={required}
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-xl bg-slate-800/50 border text-white placeholder:text-slate-500',
            'transition-all duration-200 resize-none',
            'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'text-sm px-4 py-3',
            error
              ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500'
              : 'border-white/10 hover:border-white/20',
            className
          )}
          {...props}
        />
      </FieldWrapper>
    );
  }
);

TextareaField.displayName = 'TextareaField';

// Checkbox Field
interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: string;
  description?: string;
  error?: string;
  wrapperClassName?: string;
}

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  (
    {
      label,
      description,
      error,
      className,
      wrapperClassName,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${label.toLowerCase().replace(/\s+/g, '-')}`;

    return (
      <div className={cn('flex items-start gap-3', wrapperClassName)}>
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className={cn(
            'mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-800/50',
            'text-blue-500 focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-0',
            'cursor-pointer transition-colors',
            'checked:bg-blue-500 checked:border-blue-500',
            error && 'border-red-500/50',
            className
          )}
          {...props}
        />
        <div>
          <label htmlFor={checkboxId} className="text-sm font-medium text-slate-300 cursor-pointer">
            {label}
          </label>
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
          {error && (
            <p className="text-xs text-red-400 mt-1">{error}</p>
          )}
        </div>
      </div>
    );
  }
);

CheckboxField.displayName = 'CheckboxField';

// Radio Group
interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  label?: string;
  required?: boolean;
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  required,
  name,
  options,
  value,
  onChange,
  error,
  orientation = 'vertical',
  className,
}) => {
  return (
    <FieldWrapper label={label} required={required} error={error} className={className}>
      <div
        className={cn(
          'flex gap-4',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'
        )}
        role="radiogroup"
        aria-label={label}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-start gap-3 cursor-pointer',
              option.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange?.(e.target.value)}
              disabled={option.disabled}
              className={cn(
                'mt-0.5 h-4 w-4 border-white/20 bg-slate-800/50',
                'text-blue-500 focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-0',
                'cursor-pointer transition-colors'
              )}
            />
            <div>
              <span className="text-sm font-medium text-slate-300">{option.label}</span>
              {option.description && (
                <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
              )}
            </div>
          </label>
        ))}
      </div>
    </FieldWrapper>
  );
};

// Form Group for grouping related fields
interface FormGroupProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export const FormGroup: React.FC<FormGroupProps> = ({
  title,
  description,
  children,
  className,
}) => {
  return (
    <fieldset className={cn('space-y-4', className)}>
      {title && (
        <legend className="sr-only">{title}</legend>
      )}
      {(title || description) && (
        <div className="mb-4">
          {title && (
            <h3 className="text-sm font-semibold text-white">{title}</h3>
          )}
          {description && (
            <p className="text-xs text-slate-500 mt-1">{description}</p>
          )}
        </div>
      )}
      {children}
    </fieldset>
  );
};

export default InputField;
